<script setup lang="ts">
    import { computed, reactive, watch, onMounted, onBeforeUnmount, inject, h, type ComputedRef } from 'vue';
    import Loading from '@shared/components/Loading.vue';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import { getResponse, downloadAllSurveyResources, getSurveyResourcePreviewUrl } from '../SurveyStatistics.access';
    import {
        QuestionType,
        QuestionOptionsDisplay,
        type Question,
        type SurveyResponseDetail,
        type EventParticipantAnswerData,
        type AttestationAnswer,
    } from '../../SurveysTypes.d';
    import QuestionMetaBadges from './QuestionMetaBadges.vue';
    import QuestionAnswerGlyphiconOptions from './QuestionAnswerGlyphiconOptions.vue';
    import QuestionAnswerChoiceOptions from './QuestionAnswerChoiceOptions.vue';
    import QuestionAnswerFreeText from './QuestionAnswerFreeText.vue';
    import QuestionAnswerMultipleNumeric from './QuestionAnswerMultipleNumeric.vue';
    import QuestionAnswerEventParticipant from './QuestionAnswerEventParticipant.vue';
    import QuestionAnswerAttestation from './QuestionAnswerAttestation.vue';
    import QuestionAnswerFileUpload from './QuestionAnswerFileUpload.vue';
    import { orderQuestionsWithChildren } from '../SurveyStatistics.utils';

    const props = defineProps<{
        surveyId: string;
        responseId: string;
    }>();

    const questions = inject<ComputedRef<Question[]>>(
        'surveyStatisticsQuestions',
        computed(() => [])
    );

    const surveyTitle = inject<ComputedRef<string>>(
        'surveyStatisticsTitle',
        computed(() => '')
    );

    const state = reactive<{
        response: SurveyResponseDetail | null;
        loading: boolean;
    }>({
        response: null,
        loading: false,
    });

    const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'svg'];

    function isImageFile(fileName: string): boolean {
        const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
        return IMAGE_EXTENSIONS.includes(extension);
    }

    const resourcePreviewUrls = reactive<Record<string, string>>({});

    function clearResourcePreviewUrls() {
        Object.values(resourcePreviewUrls).forEach((url) => window.URL.revokeObjectURL(url));
        Object.keys(resourcePreviewUrls).forEach((key) => delete resourcePreviewUrls[key]);
    }

    const RESOURCE_PREVIEW_CONCURRENCY = 4;

    async function loadResourcePreviews() {
        if (!state.response) return;

        const resources = state.response.SurveyAnswers.flatMap((answer) =>
            answer.SurveyAnswerResources.filter((resource) => isImageFile(resource.OriginalFileName))
        );

        let nextIndex = 0;
        async function worker() {
            while (nextIndex < resources.length) {
                const resource = resources[nextIndex++];
                const url = await getSurveyResourcePreviewUrl(resource.ResourceId, resource.OriginalFileName);
                resourcePreviewUrls[resource.ResourceId] = url;
            }
        }

        await Promise.all(Array.from({ length: Math.min(RESOURCE_PREVIEW_CONCURRENCY, resources.length) }, worker));
    }

    async function load() {
        state.loading = true;
        clearResourcePreviewUrls();
        try {
            state.response = await getResponse(props.surveyId, props.responseId);
            loadResourcePreviews();
        } finally {
            state.loading = false;
        }
    }

    watch(() => props.responseId, load);
    onMounted(load);
    onBeforeUnmount(clearResourcePreviewUrls);

    const atleastOneResource = computed(() => !!state.response?.SurveyAnswers.some((a) => a.SurveyAnswerResources.length > 0));

    const GUID_EMPTY = '00000000-0000-0000-0000-000000000000';

    function contactName(): string {
        const contact = state.response?.Contact;
        if (!contact) return '';
        return [contact.FirstName, contact.LastName].filter(Boolean).join(' ') || contact.Email || $Resources.YourContactLabel;
    }

    function ResponseTitle() {
        return [
            `${$Resources.HereIsContactsResponseFirstPartLabel} `,
            h('i', contactName()),
            `${$Resources.HereIsContactsResponseSecondPartLabel} `,
            h('i', surveyTitle.value),
        ];
    }

    function contactRoute() {
        return {
            name: 'SurveyStatisticsResponseContact',
            params: { surveyId: props.surveyId, contactId: state.response?.Contact.ContactId },
        };
    }

    function parseEventParticipantAnswer(freeText: string | null): EventParticipantAnswerData | null {
        if (!freeText) return null;
        try {
            return JSON.parse(freeText);
        } catch {
            return null;
        }
    }

    function parseAttestationAnswer(freeText: string | null): AttestationAnswer | null {
        if (!freeText) return null;
        const parts = freeText.split(';');
        const name = [parts[0], parts[1]].filter((s) => s && s.trim()).join(' ');
        const email = (parts[2] ?? '').trim();
        const message = (parts[3] ?? '').trim();
        return { name, email, message };
    }

    const nonContentQuestionTypes = [QuestionType.Content, QuestionType.Pagebreak, QuestionType.Divider];

    const answeredQuestions = computed(() => {
        if (!state.response) return [];

        const contentQuestions = questions.value.filter((question) => !nonContentQuestionTypes.includes(question.Type));
        const orderedQuestions = orderQuestionsWithChildren(contentQuestions);

        let main = 0;
        const subCounters: Record<string, number> = {};

        return orderedQuestions.map((question, index) => {
            const answer = state.response!.SurveyAnswers.find((a) => a.Question.QuestionId === question.QuestionId);
            const answeredOptionIds = new Set((answer?.Question.QuestionOptions ?? []).map((o) => o.QuestionOptionId));
            const answered = question.Type === QuestionType.FileUpload ? !!answer && (answer.SurveyAnswerResources?.length ?? 0) > 0 : !!answer;

            let label: string;
            if (question.ParentQuestionId) {
                subCounters[question.ParentQuestionId] = (subCounters[question.ParentQuestionId] || 0) + 1;
                label = `${main}.${subCounters[question.ParentQuestionId]}`;
            } else {
                main++;
                label = `${main}.`;
            }

            const previous = orderedQuestions[index - 1];
            const next = orderedQuestions[index + 1];

            return {
                question,
                label,
                isMatrixHeader: question.Type === QuestionType.Matrix && !question.ParentQuestionId,
                isMatrixChild: !!question.ParentQuestionId,
                isFirstMatrixChild: !!question.ParentQuestionId && previous?.ParentQuestionId !== question.ParentQuestionId,
                isLastMatrixChild: !!question.ParentQuestionId && next?.ParentQuestionId !== question.ParentQuestionId,
                answered,
                freeText: answer?.FreeText ?? null,
                numericOptions:
                    question.Type === QuestionType.MultipleNumeric
                        ? [...(question.QuestionOptions ?? [])]
                              .sort((a, b) => a.Ordinal - b.Ordinal)
                              .map((option) => ({
                                  QuestionOptionId: option.QuestionOptionId,
                                  Value: option.Value,
                                  NumericResponseValue:
                                      answer?.Question.QuestionOptions.find((o) => o.QuestionOptionId === option.QuestionOptionId)?.NumericResponseValue ??
                                      null,
                              }))
                        : [],
                eventParticipants: question.Type === QuestionType.EventParticipant ? parseEventParticipantAnswer(answer?.FreeText ?? null) : null,
                attestation: question.Type === QuestionType.Attestation ? parseAttestationAnswer(answer?.FreeText ?? null) : null,
                resources: answer?.SurveyAnswerResources ?? [],
                answeredOptionIds,
            };
        });
    });
</script>

<template>
    <template v-if="state.response">
        <header class="list-header">
            <h2>
                <component :is="ResponseTitle" />
            </h2>

            <ul class="list-unstyled d-flex gap-lg flex-wrap">
                <li v-if="state.response.Contact.ContactId === GUID_EMPTY">
                    <small>{{ $Resources.ResponseRespondent }}:</small>
                    <span class="fw-600 d-block">{{ $Resources.AnonymousNameLabel }}</span>
                </li>
                <li v-else>
                    <small>{{ $Resources.ResponseRespondent }}:</small>
                    <router-link class="fw-600 d-block" :to="contactRoute()">{{ contactName() }}</router-link>
                </li>

                <li v-if="state.response.CultureEnglishName">
                    <small>{{ $Resources.SurveyStatisticsCulturesChartHeading }}:</small>
                    <span class="fw-600 d-block">{{ state.response.CultureEnglishName }}</span>
                </li>
                <li>
                    <small>{{ $Resources.ResponseCompleted }}:</small>
                    <span class="fw-600 d-block">{{ formatDateTime(state.response.Created) }}</span>
                </li>
            </ul>

            <div class="form-group m-sm-b" v-if="atleastOneResource">
                <button type="button" class="btn btn-default btn-sm" @click="downloadAllSurveyResources(props.responseId)">
                    {{ $Resources.DownloadAllResponseFiles }}
                </button>
            </div>
        </header>

        <div class="container-fluid m-lg-t m-xs-b">
            <template v-for="entry in answeredQuestions" :key="entry.question.QuestionId">
                <h3 v-if="entry.isMatrixHeader" class="m-0-t matrix-group-header">
                    {{ entry.label }} {{ entry.question.Title }}
                    <small class="fw-600">{{ $Resources.QuestionTypeMatrixLabel }}</small>
                    <small v-if="entry.question.Required">* {{ $Resources.QuestionRequiredLabel }}</small>
                </h3>

                <article
                    v-else
                    class="question-card"
                    :class="{
                        nested: entry.question.ParentQuestionId,
                        'matrix-child': entry.isMatrixChild,
                        'matrix-child-first': entry.isFirstMatrixChild,
                        'matrix-child-last': entry.isLastMatrixChild,
                    }">
                    <header>
                        <QuestionMetaBadges :question="entry.question" :answered="entry.answered" :show-average="true" />

                        <h3 class="m-0-t">{{ entry.label }} {{ entry.question.Title }}</h3>
                    </header>

                    <div v-if="entry.question.Text" class="m-sm-b" v-html="entry.question.Text"></div>

                    <QuestionAnswerGlyphiconOptions
                        v-if="entry.question.OptionsDisplay === QuestionOptionsDisplay.Glyphicon && entry.question.QuestionOptions?.length"
                        :question="entry.question"
                        :answered-option-ids="entry.answeredOptionIds" />

                    <QuestionAnswerChoiceOptions
                        v-else-if="
                            [QuestionType.Single, QuestionType.Multiple, QuestionType.Matrix].includes(entry.question.Type) &&
                            entry.question.QuestionOptions?.length
                        "
                        :question="entry.question"
                        :answered-option-ids="entry.answeredOptionIds" />

                    <QuestionAnswerFreeText
                        v-if="[QuestionType.FreeText, QuestionType.InputField, QuestionType.Hidden].includes(entry.question.Type)"
                        :free-text="entry.freeText" />

                    <QuestionAnswerMultipleNumeric v-if="entry.question.Type === QuestionType.MultipleNumeric" :numeric-options="entry.numericOptions" />

                    <QuestionAnswerEventParticipant
                        v-if="entry.question.Type === QuestionType.EventParticipant"
                        :event-participants="entry.eventParticipants" />

                    <QuestionAnswerAttestation v-if="entry.question.Type === QuestionType.Attestation" :attestation="entry.attestation" />

                    <QuestionAnswerFileUpload
                        v-if="entry.question.Type === QuestionType.FileUpload"
                        :response-id="props.responseId"
                        :question-id="entry.question.QuestionId"
                        :resources="entry.resources"
                        :resource-preview-urls="resourcePreviewUrls"
                        :is-image-file="isImageFile" />
                </article>
            </template>
        </div>
    </template>

    <Loading :show="state.loading" loadingStyle="progress-bar" fixed></Loading>
</template>

<style lang="less">
    .text-answer {
        padding: var(--ug-spacing-sm) var(--ug-spacing-md);
        border-radius: var(--border-radius);
        background: var(--brand-secondary-50);
        min-height: 40px;
        white-space: pre-wrap;
        margin-bottom: var(--ug-spacing-sm);
    }
</style>
